import { onCleanup, onMount } from 'solid-js'
import { getGsap, prefersReducedMotion } from '~/utils/gsap'

export interface RevealOptions {
  /** Selector de hijos a revelar con stagger. Si se omite, revela el propio elemento. */
  target?: string
  y?: number
  x?: number
  scale?: number
  duration?: number
  stagger?: number
  once?: boolean
  ease?: string
  delay?: number
}

/**
 * Directiva use:reveal — revelado al hacer scroll, animado con GSAP.
 *
 * Usa IntersectionObserver (no ScrollTrigger) porque en este proyecto ScrollTrigger
 * dejaba secciones en blanco (posiciones de trigger obsoletas tras cambios de layout y
 * scroll programático). IO dispara de forma fiable conforme cada bloque entra en pantalla.
 *
 * Fluidez / sin estrés:
 *  - Dispara ~120px ANTES de entrar (rootMargin) → en scroll rápido ya está animando.
 *  - threshold 0 (primer píxel) + animación corta con easing suave + stagger en hijos.
 */
export function reveal(el: HTMLElement, accessor: () => RevealOptions | boolean | undefined) {
  onMount(() => {
    const raw = accessor?.()
    const o: RevealOptions = !raw || raw === true ? {} : raw
    const g = getGsap()
    const targets = o.target ? Array.from(el.querySelectorAll<HTMLElement>(o.target)) : [el]

    if (!g || prefersReducedMotion() || targets.length === 0) return

    g.set(targets, { opacity: 0, y: o.y ?? 32, x: o.x ?? 0, scale: o.scale ?? 1, willChange: 'transform, opacity' })

    let done = false
    const animateIn = () => {
      if (done) return
      done = true
      g.to(targets, {
        opacity: 1,
        y: 0,
        x: 0,
        scale: 1,
        duration: o.duration ?? 0.6,
        ease: o.ease ?? 'power3.out',
        stagger: o.stagger ?? 0.1,
        delay: o.delay ?? 0,
        overwrite: 'auto',
        clearProps: 'willChange,transform,opacity,scale'
      })
    }

    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            animateIn()
            if (o.once !== false) io.unobserve(el)
          }
        }
      },
      { threshold: 0, rootMargin: '0px 0px 120px 0px' }
    )
    io.observe(el)

    onCleanup(() => io.disconnect())
  })
}

export default reveal
